// Intel severity helpers used by the Timeline view (and other surfaces
// that render the same dense row shape). The original `INTEL_COLUMNS`
// + `makeIntelColumns` factory was deleted in the simplify pass — Timeline
// declares its own VirtualTable columns inline against `severityFor` /
// `SEVERITY_COLOR`, so the indirection bought nothing.
//
// SEVERITY_COLOR maps the 5-bucket contract enum onto the heat ramp tokens
// (see apps/terminal/src/styles/tokens.css). Boundaries here mirror
// severityRamp() in packages/intel-feed-client/src/colors.ts:
//   HOT      ≥90   --sev-hot
//   HIGH     70-89 --sev-elevated  (legend label: "ELEVATED")
//   MED      40-69 --sev-watch     (legend label: "WATCH")
//   LOW      20-39 --sev-ok        (legend label: "OK", lighter weight)
//   INFO     <20   --sev-ok (muted via the dim fg-3 channel)
// The contract enum stays 5 buckets so existing rows / relevance.bucket
// values still round-trip; the legend collapses LOW+INFO into the visual
// "OK" band to keep operator cognitive load down.

import type { intel } from '@eidollon/contracts';

export const SEVERITY_COLOR: Record<intel.Severity, string> = {
  HOT: 'var(--sev-hot)',
  HIGH: 'var(--sev-elevated)',
  MED: 'var(--sev-watch)',
  LOW: 'var(--sev-ok)',
  INFO: 'var(--fg-3)',
};

/** Map numeric severity (0..100) → 5-bucket label. Mirrors the contract used
 *  by `relevance.bucket` so a row with both keys lands on the same color. */
export function severityBucket(sev: number): intel.Severity {
  if (sev >= 90) return 'HOT';
  if (sev >= 70) return 'HIGH';
  if (sev >= 40) return 'MED';
  if (sev >= 20) return 'LOW';
  return 'INFO';
}

export function severityFor(row: intel.IntelSignal): intel.Severity {
  return row.relevance?.bucket ?? severityBucket(row.severity);
}
